import { getSameAttribute, removeNull } from '@/utils/handleData.js'
import { getLaborParentBidUid } from '@/utils/bidLevelActions.js'
import { finishAiDraftIfNeeded } from '@/utils/aiworkbench'
import { buildTrainingScanQcodeInfo } from '@/utils/scanQrcode'
import { trainingListDisabledButtons } from '@/utils/trainingPhase'
import { resolveSubmitPlanTime } from '@/utils/pageParams/preClassEducation'

// 后端 trainingVideos 可能是数组、JSON 字符串或 PG 数组字符串 {uid1,uid2}
function parseTrainingVideos(value) {
  if (value == null || value === '') return []
  let list = []
  if (Array.isArray(value)) {
    list = value
  } else if (typeof value === 'string') {
    const text = value.trim()
    if (!text || text === '{}' || text === '[]' || text === 'null') return []
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) {
        list = parsed
      } else if (parsed != null && parsed !== '') {
        list = [String(parsed)]
      }
    } catch (e) {
      if (text.startsWith('{') && text.endsWith('}')) {
        const inner = text.slice(1, -1).trim()
        if (!inner) return []
        list = inner.split(',').map((item) => item.trim().replace(/^"(.*)"$/, '$1'))
      } else {
        list = [text]
      }
    }
  }
  return list
    .map((item) => sanitizeVideoUid(item))
    .filter((item) => item && item !== 'null' && item !== 'NULL' && item !== '{}' && item !== '[]' && item !== '暂无影像')
}

function sanitizeVideoUid(item) {
  if (item == null) return ''
  let value = String(item).trim()
  while (value && '{["\''.indexOf(value.charAt(0)) !== -1) {
    value = value.slice(1).trim()
  }
  while (value && '}]"\''.indexOf(value.charAt(value.length - 1)) !== -1) {
    value = value.slice(0, -1).trim()
  }
  return value
}

function isEduTrainingVideoCleared(row) {
  if (!row || !parseTrainingVideos(row.trainingVideos).length) return false
  const time = row.trainingTime || row.training_time
  if (!time) return false
  const plan = time instanceof Date ? time : new Date(String(time).replace(/-/g, '/'))
  if (isNaN(plan.getTime())) return false
  const expire = new Date(plan.getTime())
  expire.setMonth(expire.getMonth() + 1)
  return Date.now() >= expire.getTime()
}

// 后端 trainingVideos 为 String[]（PG varchar[]），保证提交为数组
function normalizeEduClassPayload(data) {
  const normalizeOne = (item) => {
    if (!item || typeof item !== 'object') return item
    const row = { ...item }
    if (Object.prototype.hasOwnProperty.call(item, 'trainingVideos')) {
      row.trainingVideos = parseTrainingVideos(item.trainingVideos)
    } else {
      delete row.trainingVideos
    }
    if (!row.trainingTime) {
      row.trainingTime = null
    } else {
      row.trainingTime = resolveSubmitPlanTime(row.trainingTime)
    }
    if (!row.executeTime) row.executeTime = null
    return row
  }
  return Array.isArray(data) ? data.map(normalizeOne) : normalizeOne(data)
}

function applyEduOrg(item, orgValue) {
  if (!item || typeof item !== 'object') return item
  item.operateOrgUid = orgValue || ''
  if (!item.createOrgUid) item.createOrgUid = orgValue || ''
  return item
}

function withEduOrg(data, orgValue) {
  const payload = normalizeEduClassPayload(data)
  return Array.isArray(payload)
    ? payload.map((item) => applyEduOrg(item, orgValue))
    : applyEduOrg(payload, orgValue)
}

function decoratePreClassEduItem(item, orgType, bids) {
  if (!item) return item
  item.disabledButtons = []
  item.createOrgUid = item.createOrgUid || item.create_org_uid || ''
  item.parentUid = item.parentUid || item.parent_uid || ''
  item.trainingVideos = parseTrainingVideos(item.trainingVideos)
  item.eduVideoExpired = isEduTrainingVideoCleared(item)
  item.videos = item.eduVideoExpired
    ? '过期清除'
    : item.trainingVideos.length
    ? item.trainingVideos.length + '个影像'
    : '暂无影像'
  item.attendanceCount = Number(item.checkInNum || 0)
  if (item.isDone != null && item.isDone !== '' && !isNaN(Number(item.isDone))) {
    item.attendanceCount = Number(item.isDone)
  }
  const onSiteCount = Number(item.onSiteCount || item.onsitecount || 0)
  item.coverageRate =
    onSiteCount > 0
      ? ((item.attendanceCount / onSiteCount) * 100).toFixed(1) + '%'
      : item.attendanceCount > 0
      ? '100.0%'
      : '0.0%'
  const hasVideo = item.trainingVideos.length > 0
  const stored = String(item.executeStatus || '').trim()
  const rectify =
    stored === '未整改' ||
    stored === '已整改' ||
    !!String(item.parentUid || item.parent_uid || '').trim()
  item.executeStatus = hasVideo ? (rectify ? '已整改' : '已执行') : rectify ? '未整改' : '未执行'
  item.isDone = item.executeStatus
  if (!hasVideo) item.executeTime = ''
  item.eduTime = ['计划：' + (item.trainingTime || '—'), '执行：' + (item.executeTime || '—')].join(
    '\n'
  )
  if (!item.bidName) {
    const bid = (bids || []).find(
      (b) => String(b.value || b.orgUid) === String(item.orgUid)
    )
    item.bidName = bid
      ? bid.orgCode || bid.orgName || String(bid.label || '').replace(/（[^）]*）\s*$/, '')
      : ''
  }
  item.detailForm = [
    {
      prop: 'trainingNo',
      label: '培训编号',
      value: item.trainingNo,
      span: 12
    }
  ]
  if (orgType !== 'simPro' && orgType !== 'laborCom') {
    item.detailForm.push({
      prop: 'bidName',
      label: '项目标段',
      value: item.bidName,
      span: 12
    })
  }
  if (orgType !== 'laborCom') {
    item.detailForm.push({
      prop: 'laborCompanyName',
      label: '劳务公司名称',
      value: item.laborCompanyName,
      span: 12
    })
  }
  item.detailForm.push(
    {
      prop: 'teamName',
      label: '班组名称',
      value: item.teamName,
      span: 12
    },
    {
      prop: 'trainingTime',
      label: '计划培训时间',
      value: item.trainingTime,
      span: 12
    },
    {
      prop: 'executeTime',
      label: '执行培训时间',
      value: item.executeTime,
      span: 12
    },
    {
      prop: 'trainingPerson',
      label: '执行人',
      value: item.trainingPerson,
      span: 12
    },
    {
      prop: 'attendanceCount',
      label: '签到人数',
      value: item.attendanceCount,
      span: 12
    },
    {
      prop: 'coverageRate',
      label: '人员覆盖率',
      value: item.coverageRate,
      span: 12
    },
    {
      prop: 'workPlace',
      label: '作业部位',
      value: item.workPlace,
      span: 12
    },
    {
      prop: 'workContent',
      label: '作业内容',
      value: item.workContent,
      span: 24
    },
    {
      prop: 'trainingBrief',
      label: '教育内容',
      value: item.trainingBrief,
      span: 24
    }
  )
  if (Array.isArray(item.children) && item.children.length) {
    item.children = item.children.map((child) =>
      decoratePreClassEduItem(child, orgType, bids)
    )
  } else {
    item.children = null
  }
  return item
}

export default {
  namespaced: true,
  state: {
    trainingList: {}, // 教育培训列表
    trainingInfo: {}, // 教育培训信息
    preClassEduList: {}, // 班前教育列表
    preClassEduInfo: {}, // 班前教育信息
    teamPendingError: '', // 同班组今日未执行校验错误文案
    trainingMaterials: {}, // 培训资料列表（本机构 / 超管资料库）
    publicMaterials: {}, // 公共资料库弹窗列表
    trainingMaterialInfo: {}, // 培训资料信息
    materialLearnRecords: {} // 资料浏览量下钻学习记录
  },
  actions: {
    // 获取教育培训列表
    async getTrainingList({ rootState, commit, dispatch }) {
      var queryValues = await dispatch(
        'bidSection/ensureDefaultBidQuery',
        { queryKey: 'training', fieldKey: 'orgUid' },
        { root: true }
      )
      queryValues = Object.assign({}, queryValues)
      queryValues.page = rootState.page.training ? rootState.page.training : 1
      queryValues.limit = rootState.limit.training
        ? rootState.limit.training
        : 10
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/educationtraininglist',
          data: queryValues
        },
        { root: true }
      )
      // console.log(res)
      var trainingList = res.data ? res.data : {}
      trainingList.result.forEach((item) => {
        const signedCount = Number(item.isDone || 0)
        item.signedCount = signedCount
        item.answerCount = Number(item.answerCount || 0)
        const isSuperAdmin =
          rootState.user.userName === rootState.superAdminName ||
          rootState.user.personName === rootState.superAdminName
        item.disabledButtons = trainingListDisabledButtons(item, {
          otherOrg: !!(item.orgUid && item.orgUid !== rootState.user.orgValue),
          superAdmin: isSuperAdmin
        })
        const duration = item.trainingDuration
          ? String(item.trainingDuration).includes('分钟')
            ? item.trainingDuration
            : item.trainingDuration + '分钟'
          : ''
        item.trainingDuration = duration
        item.trainingTimeText = [
          '计划：' + (item.trainingTime || '—'),
          '执行：' + (item.executeTime || '—')
        ].join('\n')
        item.isDone = signedCount
          ? '已执行'
          : item.trainingVideos
          ? '未签到'
          : '未执行'
        item.trainingPics = parseTrainingVideos(item.trainingPics)
        item.trainingVideos = parseTrainingVideos(item.trainingVideos)
        item.preview =
          item.trainingVideos.length || item.trainingPics.length
            ? '查看影像'
            : '暂无影像'
        commit(
          'pictureSet',
          { key: 'trainingPics', value: item.trainingPics },
          { root: true }
        )
        item.fileLists = {
          trainingPics: rootState.pictures.trainingPics || [],
          trainingVideos: []
        }
        if (item.trainingVideos.length) {
          item.fileLists.trainingVideos = item.trainingVideos.map((uid, index) => {
            return {
              uid: uid,
              videoUid: uid,
              name: '培训视频' + (index + 1),
              type: 'application/x-mpegURL'
            }
          })
        }
        item.srcList = getSameAttribute(
          item.fileLists.trainingPics,
          'url'
        ).values
        item.qcodeInfo = buildTrainingScanQcodeInfo(item)
        item.detailForm = [
          { prop: 'trainingNo', label: '培训编号', value: item.trainingNo },
          { prop: 'trainingName', label: '培训名称', value: item.trainingName },
          { prop: 'trainingType', label: '培训类型', value: item.trainingType },
          {
            prop: 'trainingTime',
            label: '计划培训时间',
            value: item.trainingTime
          },
          {
            prop: 'executeTime',
            label: '执行培训时间',
            value: item.executeTime || '—'
          },
          {
            prop: 'trainingPlace',
            label: '培训地点',
            value: item.trainingPlace
          },
          {
            prop: 'trainingDuration',
            label: '培训时长',
            value: item.trainingDuration
          },
          {
            prop: 'trainingPerson',
            label: '培训人',
            value: item.trainingPerson
          },
          {
            prop: 'trainingMethods',
            label: '培训方式',
            value: item.trainingMethods
          },
          {
            prop: 'examineType',
            label: '考核方式',
            value: item.examineType
          },
          {
            prop: 'isDone',
            label: '是否执行',
            value: item.isDone
          }
        ]
      })
      commit(
        'baseSet',
        {
          module: 'training',
          key: 'trainingList',
          value: trainingList
        },
        { root: true }
      )
    },
    // 新增教育培训
    async addTraining({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/cultivate/educationtraining',
          data: data,
          msg: '新增教育培训成功'
        },
        { root: true }
      )
      return res
    },
    // 获取教育培训详情
    async getTrainingInfo({ state, rootState, commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/educationtraining',
          data: { uid: uid ? uid : state.trainingInfo.uid }
        },
        { root: true }
      )
      if (!res || res.code !== 200) {
        return {}
      }
      var trainingInfo = res.data ? res.data : {}
      trainingInfo.trainingBrief = trainingInfo.trainingBrief || ''
      trainingInfo.trainingPerson = trainingInfo.trainingPerson
        ? trainingInfo.trainingPerson
        : ''
      trainingInfo.trainingPersons = trainingInfo.trainingPerson
        ? trainingInfo.trainingPerson.split('、')
        : []
      trainingInfo.paperUid = Array.isArray(trainingInfo.paperUid)
        ? trainingInfo.paperUid
        : trainingInfo.paperUid
          ? [trainingInfo.paperUid]
          : []
      trainingInfo.trainingMaterials = Array.isArray(
        trainingInfo.trainingMaterials
      )
        ? trainingInfo.trainingMaterials
        : trainingInfo.trainingMaterials
          ? [trainingInfo.trainingMaterials]
          : []
      trainingInfo.trainingMaterialsInfo = Array.isArray(
        trainingInfo.trainingMaterialsInfo
      )
        ? trainingInfo.trainingMaterialsInfo
        : []
      trainingInfo.trainingMaterialsInfo.forEach((item) => {
        if (!item) return
        const formatItem =
          item.dataFormat && Array.isArray(rootState.dictionarys.dataFormat)
            ? rootState.dictionarys.dataFormat.find((format) => {
                return format.value === item.dataFormat
              })
            : null
        item.dataFormatName = formatItem ? formatItem.label : ''
        const dataType = Array.isArray(item.dataType) ? item.dataType : []
        const applicableUsers = Array.isArray(item.applicableUsers)
          ? item.applicableUsers
          : []
        item.detailForm = [
          {
            prop: 'files',
            label: '关联文件',
            drillDown: true,
            value: item.dataName
          },
          {
            prop: 'dataAccess',
            label: '资料权限',
            value: item.dataAccess
          },
          { prop: 'usageCount', label: '使用次数', value: item.usageCount },
          { prop: 'pageviews', label: '浏览量', value: item.pageviews },
          {
            prop: 'dataType',
            label: '资料类型',
            value: dataType.join('、'),
            span: 12
          },
          {
            prop: 'dataLabels',
            label: '资料标签',
            value: item.dataLabels,
            span: 12
          },
          {
            prop: 'applicableUsers',
            label: '适用对象',
            value: applicableUsers.join('、'),
            span: 24
          }
        ]
      })
      trainingInfo.trainingMaterial = getSameAttribute(
        trainingInfo.trainingMaterialsInfo,
        'dataName',
        '；\n'
      ).joinString
      trainingInfo.paperInfo = Array.isArray(trainingInfo.paperInfo)
        ? trainingInfo.paperInfo
        : []
      trainingInfo.paperInfo.forEach((item) => {
        if (!item) return
        if (Array.isArray(item.paperType)) {
          item.paperType = item.paperType.join('、')
        } else {
          item.paperType = item.paperType || ''
        }
        item.examinationLabels =
          '【' +
          item.paperType +
          (item.examinationLabels ? '、' + item.examinationLabels : '') +
          '】'
        const applicableUsers = Array.isArray(item.applicableUsers)
          ? item.applicableUsers
          : []
        item.applicableUsers = '【' + applicableUsers.join('、') + '】'
        item.detailForm = [
          {
            prop: 'applicableUsers',
            label: '适用对象',
            value: item.applicableUsers,
            span: 24
          },
          {
            prop: 'examinationLabels',
            label: '试卷类型及考点标签',
            value: item.examinationLabels,
            fontBold: true,
            span: 24
          },
          {
            prop: 'questionInfo',
            label: '试题信息',
            value: '查看试题详情',
            drillDown: true,
            span: 24
          },
          {
            prop: 'paperDescription',
            label: '试卷简述',
            value: item.paperDescription,
            span: 24
          }
        ]
      })
      trainingInfo.paperUids = getSameAttribute(
        trainingInfo.paperInfo,
        'paperTitle',
        '；\n'
      ).joinString
      trainingInfo.shouldArrivePeople = Array.isArray(
        trainingInfo.shouldArrivePeople
      )
        ? trainingInfo.shouldArrivePeople
        : []
      trainingInfo.shouldArrivePeoples = getSameAttribute(
        trainingInfo.shouldArrivePeople,
        'personName',
        '、'
      ).joinString
      trainingInfo.executeTime = trainingInfo.executeTime || ''
      trainingInfo.trainingPics = parseTrainingVideos(trainingInfo.trainingPics)
      trainingInfo.trainingVideos = parseTrainingVideos(trainingInfo.trainingVideos)
      commit(
        'pictureSet',
        { key: 'trainingPics', value: trainingInfo.trainingPics },
        { root: true }
      )
      trainingInfo.fileLists = {
        trainingPics: rootState.pictures.trainingPics || [],
        trainingVideos: trainingInfo.trainingVideos.map((uid, index) => {
          return {
            uid: uid,
            videoUid: uid,
            name: '培训视频' + (index + 1),
            type: 'application/x-mpegURL'
          }
        })
      }
      trainingInfo.srcList = getSameAttribute(
        trainingInfo.fileLists.trainingPics,
        'url'
      ).values

      commit(
        'baseSet',
        {
          module: 'training',
          key: 'trainingInfo',
          value: trainingInfo
        },
        { root: true }
      )
      return trainingInfo
    },
    // 编辑教育培训任务
    async editTraining({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/cultivate/educationtraining',
          data: data,
          msg: '编辑教育培训成功'
        },
        { root: true }
      )
      return res
    },
    // 删除教育培训
    async deleteTraining({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/cultivate/educationtraining',
          data: { uid: uid },
          msg: '删除教育培训成功'
        },
        { root: true }
      )
      return res
    },
    // 获取班前教育列表
    async getPreClassEduList({ rootState, rootGetters, commit, dispatch }) {
      // 班前教育不套用默认标段：企业/工程级未选手动标段时，用当前机构查下级全部标段
      const query = Object.assign(
        {},
        rootState.queryValues.preClassEducation || {}
      )
      const orgType = rootGetters['user/orgType']
      const orgValue = rootState.user.orgValue
      let orgUid = query.orgUid || ''
      let laborCompanyUid = query.laborCompanyUid
      if (orgType === 'laborCom') {
        laborCompanyUid = laborCompanyUid || orgValue
        orgUid =
          getLaborParentBidUid({
            orgType: orgType,
            orgValue: orgValue,
            state: rootState,
            getters: rootGetters
          }) || orgUid
      } else if (orgType === 'simPro') {
        // 标段级始终查当前标段，避免企业级筛选项残留导致进标段后查错/查空
        orgUid = orgValue
        if (query.orgUid !== orgValue) {
          commit(
            'queryValuesSet',
            {
              key: 'preClassEducation',
              value: Object.assign({}, query, { orgUid: orgValue })
            },
            { root: true }
          )
        }
      } else if (orgType === 'com' || orgType === 'bigPro') {
        // 未选具体标段时传企业/工程 uid，后端 scopedEduBidUids 覆盖下级全部标段
        // 若残留值等于当前企业/工程自身，视为未选（查下级全部标段）
        if (!orgUid || String(orgUid) === String(orgValue)) {
          orgUid = orgValue
        }
      } else if (!orgUid) {
        orgUid = orgValue
      }
      var queryValues = {
        orgUid: orgUid,
        laborCompanyUid: laborCompanyUid,
        teamUid: query.teamUid,
        query: query.query,
        startTime: (query.trainingTime || [])[0],
        endTime: (query.trainingTime || [])[1],
        isDone: query.isDone,
        page: rootState.page.preClassEducation
          ? rootState.page.preClassEducation
          : 1,
        limit: rootState.limit.preClassEducation
          ? rootState.limit.preClassEducation
          : 10
      }
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/educlasslist',
          data: queryValues
        },
        { root: true }
      )
      // console.log(res)
      var preClassEduList = res.data ? res.data : {}
      if (!Array.isArray(preClassEduList.result)) {
        preClassEduList.result = []
      }
      const bids = rootState.bidSection.subBidSection || []
      preClassEduList.result = preClassEduList.result.map((item) => {
        return decoratePreClassEduItem(item, orgType, bids)
      })
      commit(
        'baseSet',
        {
          module: 'training',
          key: 'preClassEduList',
          value: preClassEduList
        },
        { root: true }
      )
    },
    // 班前教育签到抽屉：同时拉取已签到记录和未退场人员
    async getPreClassSignDrawer({ dispatch }, row) {
      const event = row && typeof row === 'object' ? row : { uid: row }
      const tasks = [dispatch('signIn/getSignInList', event.uid, { root: true })]
      const laborCompanyUid = event.laborCompanyUid || event.laborTeamUid
      if (laborCompanyUid) {
        tasks.push(
          dispatch('contractor/getNoExitName', laborCompanyUid, { root: true })
        )
      }
      await Promise.all(tasks)
      return event
    },
    // 新增班前教育
    async addPreClassEducation({ dispatch, rootState }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/cultivate/educlass',
          data: withEduOrg(data, rootState.user.orgValue),
          msg: '新增班前教育成功'
        },
        { root: true }
      )
      if (res && res.code === 200) finishAiDraftIfNeeded()
      return res
    },
    // 获取班前教育详情
    async getPreClassEducationInfo({ state, commit, dispatch, rootState }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/educlass',
          data: { uid: uid ? uid : state.preClassEduInfo.uid }
        },
        { root: true }
      )
      // console.log(res)
      var preClassEduInfo = res && res.data ? res.data : {}
      const checkInCount = Number(
        preClassEduInfo.checkInNum != null
          ? preClassEduInfo.checkInNum
          : preClassEduInfo.isDone
      )
      preClassEduInfo.attendanceCount = isNaN(checkInCount) ? 0 : checkInCount
      if (preClassEduInfo.checkInNum == null) {
        preClassEduInfo.checkInNum = preClassEduInfo.attendanceCount
      }
      if (!preClassEduInfo.bidName && preClassEduInfo.orgUid) {
        const bids =
          (rootState.bidSection && rootState.bidSection.subBidSection) || []
        const bid = bids.find((item) => {
          return String(item.value || item.orgUid) === String(preClassEduInfo.orgUid)
        })
        preClassEduInfo.bidName = bid ? bid.label : ''
      }
      preClassEduInfo.workContents = preClassEduInfo.workContent
        ? preClassEduInfo.workContent.split(/；\r?\n|；(?![\r\n])/g)
        : []
      const reg1 = /^\d+、[\s\S]*?：$/gm
      const reg2 = /^(^|\n)[(（]\d+[)）]/gm
      const workContents = preClassEduInfo.trainingBrief
        ? preClassEduInfo.trainingBrief.match(reg1)
        : null
      let trainingContents = preClassEduInfo.trainingBrief
        ? preClassEduInfo.trainingBrief.split(reg1)
        : []
      trainingContents.shift()
      preClassEduInfo.trainingBriefs = []
      trainingContents.forEach((item, index) => {
        trainingContents[index] = (item || '')
          .replace(reg2, '')
          .split(/[；;]\r?\n|[；;](?![\r\n])/g)
        preClassEduInfo.trainingBriefs = preClassEduInfo.trainingBriefs.concat(
          trainingContents[index]
        )
      })
      if (workContents) {
        const trainingBriefs = workContents.map((item, index) => {
          const contentList = Array.isArray(trainingContents[index])
            ? trainingContents[index]
            : []
          const children = contentList.map((child, num) => {
            return child ? '（' + (num + 1) + '）' + child : ''
          })
          return item + '\n' + children.join('；\n')
        })
        preClassEduInfo.trainingBrief = trainingBriefs.join('\n')
      }
      preClassEduInfo.trainingVideos = parseTrainingVideos(
        preClassEduInfo.trainingVideos
      )
      preClassEduInfo.eduVideoExpired = isEduTrainingVideoCleared(preClassEduInfo)
      preClassEduInfo.fileLists = {}
      preClassEduInfo.fileLists.trainingVideos =
        preClassEduInfo.trainingVideos.length
          ? preClassEduInfo.trainingVideos.map((item, index) => {
              const video = {
                uid: item,
                videoUid: item,
                url: require('@/assets/img/video.webp'),
                name: '教育影像' + (index + 1),
                type: 'application/x-mpegURL'
              }
              return video
            })
          : []
      if (preClassEduInfo.trainingVideos.length) {
        const timesRes = await dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/qiniu/videotimes',
            data: { uids: preClassEduInfo.trainingVideos.join(',') },
            noErrorMsg: true
          },
          { root: true }
        )
        const timeRows = Array.isArray(timesRes && timesRes.data)
          ? timesRes.data
          : []
        const timeMap = {}
        timeRows.forEach((row) => {
          const uid = row && (row.uid || row.UID)
          const createTime = row && (row.createTime || row.createtime)
          if (uid && createTime) {
            const stamp = new Date(createTime).getTime()
            if (!isNaN(stamp)) {
              timeMap[String(uid)] = stamp
            }
          }
        })
        preClassEduInfo.fileLists.trainingVideos.forEach((item) => {
          if (timeMap[item.uid]) {
            item.lastModified = timeMap[item.uid]
          }
        })
      }
      commit(
        'baseSet',
        { module: 'training', key: 'preClassEduInfo', value: preClassEduInfo },
        { root: true }
      )
      return preClassEduInfo
    },
    // 仅更新班前教育签到人数
    async updateEduCheckInNum({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/cultivate/educlass/checkinnum',
          data: {
            uid: data.uid,
            checkInNum: data.checkInNum
          },
          msg: '签到人数已更新'
        },
        { root: true }
      )
      return res
    },
    async editPreClassEducation({ dispatch, rootState }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/cultivate/educlass',
          data: withEduOrg(data, rootState.user.orgValue),
          msg: '编辑班前教育成功'
        },
        { root: true }
      )
      if (res && res.code === 200) finishAiDraftIfNeeded()
      return res
    },
    // 删除班前教育
    async deletePreClassEducation({ dispatch, rootState }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/cultivate/educlass',
          data: { uid: uid, orgUid: rootState.user.orgValue },
          msg: '删除班前教育成功'
        },
        { root: true }
      )
      return res
    },
    // 获取培训教育资料列表（培训资料 / 超管资料库 / 公共资料库共用）
    async getTrainingMaterialsList({ rootState, commit, dispatch }, data) {
      const isPublicLibrary =
        rootState.currentStep &&
        rootState.currentStep.theme === 'publicMaterials'
      const isAdminLibrary =
        !isPublicLibrary &&
        rootState.currentStep &&
        rootState.currentStep.theme === 'materialLibrary'
      const qvKey = isPublicLibrary
        ? 'publicMaterials'
        : isAdminLibrary
        ? 'materialLibrary'
        : 'trainingMaterials'
      const storeKey = isPublicLibrary ? 'publicMaterials' : 'trainingMaterials'
      const qv = rootState.queryValues[qvKey] || {}
      const isSuperAdmin =
        rootState.user.userName === rootState.superAdminName ||
        rootState.user.personName === rootState.superAdminName
      const defaultScope = isPublicLibrary
        ? 'public'
        : isAdminLibrary
        ? 'all'
        : 'orgOwned'
      var queryValues = {
        orgUid: data
          ? data.orgUid
          : isPublicLibrary
          ? qv.orgUid || ''
          : qv.orgUid,
        scope: data
          ? data.scope
          : qv.scope || defaultScope,
        dataType: data
          ? typeof data.dataType === 'string'
            ? data.dataType
            : (data.dataType || []).join()
          : (qv.dataType || []).join(),
        applicableUsers: data
          ? typeof data.applicableUsers === 'string'
            ? data.applicableUsers
            : (data.applicableUsers || []).join()
          : (qv.applicableUsers || []).join(),
        dataFormat: data ? data.dataFormat : qv.dataFormat,
        query: data ? data.query : qv.query,
        page:
          data && data.page
            ? data.page
            : rootState.page[qvKey]
            ? rootState.page[qvKey]
            : 1,
        limit:
          data && data.limit
            ? data.limit
            : rootState.limit[qvKey]
            ? rootState.limit[qvKey]
            : 10
      }
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/gtrainingmaterialslist',
          data: queryValues
        },
        { root: true }
      )
      var trainingMaterials = res.data ? res.data : {}
      if (!Array.isArray(trainingMaterials.result)) {
        trainingMaterials.result = []
      }
      const currentOrg = rootState.user.orgValue || ''
      trainingMaterials.result.forEach((item) => {
        item.disabledButtons = []
        item.createOrgName = item.createOrgName ||
          (!item.orgUid ? '系统内置' : '')
        item.creatorName = item.creatorName || ''
        // 删除/编辑：超管可操作全部；其他用户仅可操作本机构创建的资料
        if (!isSuperAdmin && !isPublicLibrary) {
          const materialOrg = item.orgUid || ''
          if (!materialOrg || materialOrg !== currentOrg) {
            // 禁用 编辑(1)、删除(2)
            item.disabledButtons = ['', 1, 2]
          }
        }
        // 类型+标签拆成 tag 列表，不再拼接成【a、b】
        const rawTypes = Array.isArray(item.dataType)
          ? item.dataType
              .map((s) => (s == null ? '' : String(s).trim()))
              .filter(Boolean)
          : item.dataType
          ? String(item.dataType)
              .split(/[、,，]/)
              .map((s) => s.trim())
              .filter(Boolean)
          : []
        const extraLabels = item.dataLabels
          ? String(item.dataLabels)
              .split(/[、,，]/)
              .map((s) => s.trim())
              .filter(Boolean)
          : []
        item.dataLabelTags = rawTypes.concat(
          extraLabels.filter((label) => rawTypes.indexOf(label) < 0)
        )
        item.dataType = rawTypes.join('、')
        item.dataLabels = item.dataLabelTags.join('、')
        const users = Array.isArray(item.applicableUsers)
          ? item.applicableUsers
              .map((s) => (s == null ? '' : String(s).trim()))
              .filter(Boolean)
          : item.applicableUsers
          ? String(item.applicableUsers)
              .split(/[、,，]/)
              .map((s) => s.trim())
              .filter(Boolean)
          : []
        item.applicableUserTags = users
        item.applicableUsers = users.join('、')
        item.learningTime = item.learningTime
          ? item.learningTime + '分钟'
          : '——'
        item.usageCount = item.usageCount ? item.usageCount : 0
        item.pageviews = item.pageviews ? item.pageviews : 0
        const formatItem = item.dataFormat
          ? rootState.dictionarys.dataFormat.find((format) => {
              return format.value === item.dataFormat
            })
          : null
        item.dataFormatName = formatItem ? formatItem.label : ''
        item.files = item.dataFormat === 'article'
          ? (item.coverImage ? '图文(有图)' : '图文')
          : item.associatedFiles
          ? '预览或下载'
          : '暂无附件'
        item.detailForm = [
          {
            prop: 'files',
            label: '关联文件',
            drillDown: true,
            value: item.dataName
          },
          {
            prop: 'createOrgName',
            label: '创建机构',
            value: item.createOrgName
          },
          {
            prop: 'creatorName',
            label: '创建人',
            value: item.creatorName || '—'
          },
          {
            prop: 'dataAccess',
            label: '资料权限',
            value: item.dataAccess
          },
          { prop: 'usageCount', label: '使用次数', value: item.usageCount },
          { prop: 'pageviews', label: '浏览量', value: item.pageviews },
          {
            prop: 'dataLabels',
            label: '资料类型及标签',
            value: item.dataLabels,
            span: 24
          },
          {
            prop: 'applicableUsers',
            label: '适用对象',
            value: item.applicableUsers,
            span: 24
          }
        ]
      })
      if (!data && qv.usageCount) {
        trainingMaterials.result = trainingMaterials.result.filter((item) => {
          return item.usageCount >= qv.usageCount
        })
      }
      if (!data && qv.pageviews) {
        trainingMaterials.result = trainingMaterials.result.filter((item) => {
          return item.pageviews >= qv.pageviews
        })
      }
      commit(
        'baseSet',
        {
          module: 'training',
          key: storeKey,
          value: trainingMaterials
        },
        { root: true }
      )
      return trainingMaterials
    },
    // 新增培训教育资料
    async addTrainingMaterials({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/cultivate/gtrainingmaterials',
          data: data,
          msg: '新增培训教育资料成功'
        },
        { root: true }
      )
      return res
    },
    // 获取培训教育资料详情
    async getTrainingMaterialsInfo({ rootState, commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/gtrainingmaterials',
          data: { uid: uid }
        },
        { root: true }
      )
      // console.log(res)
      var trainingMaterialInfo = res.data ? res.data : {}
      const dataType = Array.isArray(trainingMaterialInfo.dataType)
        ? trainingMaterialInfo.dataType
        : []
      const applicableUsers = Array.isArray(trainingMaterialInfo.applicableUsers)
        ? trainingMaterialInfo.applicableUsers
        : []
      trainingMaterialInfo.dataTypes = dataType.join('、')
      trainingMaterialInfo.users = applicableUsers.join('、')
      const dataFormat = (rootState.dictionarys.dataFormat || []).find((format) => {
        return format.value === trainingMaterialInfo.dataFormat
      })
      trainingMaterialInfo.dataFormatName = dataFormat ? dataFormat.label : ''
      trainingMaterialInfo.fileLists = { associatedFiles: [] }
      if (trainingMaterialInfo.dataFormat === 'article') {
        trainingMaterialInfo.dataContent = trainingMaterialInfo.dataContent || ''
        trainingMaterialInfo.coverImage = trainingMaterialInfo.coverImage || ''
      } else if (
        trainingMaterialInfo.associatedFiles &&
        trainingMaterialInfo.dataFormat === 'image'
      ) {
        commit(
          'pictureSet',
          {
            key: 'associatedFiles',
            value: [trainingMaterialInfo.associatedFiles]
          },
          { root: true }
        )
        trainingMaterialInfo.fileLists.associatedFiles =
          rootState.pictures.associatedFiles
      } else if (
        trainingMaterialInfo.associatedFiles &&
        trainingMaterialInfo.dataFormat !== 'link' &&
        trainingMaterialInfo.dataFormat !== 'image' &&
        trainingMaterialInfo.dataFormat !== 'article'
      ) {
        trainingMaterialInfo.fileLists.associatedFiles = [
          {
            uid: trainingMaterialInfo.associatedFiles,
            videoUid:
              trainingMaterialInfo.dataFormat === 'video'
                ? trainingMaterialInfo.associatedFiles
                : undefined,
            url: dataFormat ? dataFormat.url : '',
            name: trainingMaterialInfo.dataName,
            type:
              trainingMaterialInfo.dataFormat === 'video'
                ? 'application/x-mpegURL'
                : trainingMaterialInfo.dataFormat
          }
        ]
      }
      commit(
        'baseSet',
        {
          module: 'training',
          key: 'trainingMaterialInfo',
          value: trainingMaterialInfo
        },
        { root: true }
      )
      return trainingMaterialInfo
    },
    // 记录文件是否预览查看，并记录更新浏览量
    async recordMaterialsPageviews({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/gtrainingmaterialspageviews',
          data: { uid: uid }
        },
        { root: true }
      )
      return res
    },
    // 编辑培训教育资料
    async getMaterialLearnRecords({ rootState, commit, dispatch }, data) {
      const initialLoad = typeof data === 'string'
      const currentForm = rootState.formValues.materialLearnRecords || {}
      const materialUid = initialLoad
        ? String(data || '').trim()
        : String(
            (data && data.materialUid) ||
              currentForm.materialUid ||
              ''
          ).trim()
      if (!materialUid) {
        commit(
          'baseSet',
          {
            module: 'training',
            key: 'materialLearnRecords',
            value: { result: [], totalSize: 0, limit: 10, page: 1 }
          },
          { root: true }
        )
        return initialLoad ? { materialUid: '' } : {}
      }
      const page = initialLoad
        ? 1
        : Number(rootState.page.materialLearnRecords || 1)
      const limit = initialLoad
        ? 10
        : Number(rootState.limit.materialLearnRecords || 10)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/cultivate/gtrainingmateriallearnrecords',
          data: {
            materialUid,
            orgUid: (rootState.user && rootState.user.orgValue) || '',
            page,
            limit
          }
        },
        { root: true }
      )
      const pageData = (res && res.data) || {}
      const result = Array.isArray(pageData.result) ? pageData.result : []
      result.forEach((row) => {
        if (!row) return
        row.userAccount = row.userAccount || row.loginName || row.userUid || '—'
        row.userName = row.userName || row.loginName || row.userUid || '—'
        row.orgName = row.orgName || row.orgUid || '—'
      })
      commit(
        'baseSet',
        {
          module: 'training',
          key: 'materialLearnRecords',
          value: Object.assign({}, pageData, {
            result,
            totalSize: Number(pageData.totalSize || 0),
            page: Number(pageData.page || page),
            limit: Number(pageData.limit || limit)
          })
        },
        { root: true }
      )
      if (!initialLoad) return pageData
      const theme = rootState.currentStep && rootState.currentStep.theme
      const listState =
        theme === 'publicMaterials'
          ? rootState.training.publicMaterials
          : rootState.training.trainingMaterials
      const material = ((listState && listState.result) || []).find(
        (item) => item && String(item.uid) === materialUid
      )
      return {
        materialUid,
        dataName: material ? material.dataName : ''
      }
    },
    async editTrainingMaterials({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/cultivate/gtrainingmaterials',
          data: data,
          msg: '编辑培训教育资料成功'
        },
        { root: true }
      )
      return res
    },
    // 删除培训教育资料
    async deleteTrainingMaterials({ rootState, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/cultivate/gtrainingmaterials',
          data: {
            uid: uid,
            orgUid: rootState.user.orgValue || ''
          },
          msg: '删除培训教育资料成功'
        },
        { root: true }
      )
      return res
    }
  }
}
